import moment from 'moment-timezone';
import { get } from 'lodash';

import { DATE_FORMAT, FORM } from '../constants';
import { StringUtil, NumberUtil } from '../utils';
import * as AddressHelpers from './address';

const CITYSPRINT_NETWORK_CODE = 'CITYSPRINT_SAMEDAY';

function coordinatesFromQuoteValues(values, addressRootKey, coordinatesKey) {
  const stored = get(values, coordinatesKey);
  if (
    stored?.latitude != null &&
    stored?.longitude != null &&
    !Number.isNaN(Number(stored.latitude)) &&
    !Number.isNaN(Number(stored.longitude))
  ) {
    return {
      latitude: Number(stored.latitude),
      longitude: Number(stored.longitude),
    };
  }

  const addressNode = get(values, addressRootKey);
  if (
    addressNode?._coordinates?.latitude != null &&
    addressNode?._coordinates?.longitude != null
  ) {
    return addressNode._coordinates;
  }

  return AddressHelpers.formatCoordinates(addressNode);
}

const CAPACITY_KEYS = [
  'maxLengthCM',
  'maxWidthCM',
  'maxHeightCM',
  'maxWeightKG',
];

export const buildCitysprintStateFromQuickQuote = (
  values,
  networkSelection
) => {
  const f = FORM.SHIPMENT_FIELDS;
  const selectedNetworkCode = networkSelection?.networkCode;
  const prefetchedVehicleKey = networkSelection?.parcelVehicleKey;
  const hasModalServiceSelection = Boolean(
    selectedNetworkCode || prefetchedVehicleKey
  );

  const delivery = {
    addressId:
      StringUtil.str(values, FORM.FIELDS.ADDRESS_KEY.KEY) ||
      StringUtil.str(values, f.DESTINATION_ADDRESS_KEY.KEY),
    postcode: StringUtil.str(values, f.DESTINATION_POSTCODE.KEY),
    organisation: StringUtil.str(values, f.DESTINATION_ORGANISATION.KEY),
    street: StringUtil.str(values, f.DESTINATION_STREET.KEY),
    locality: StringUtil.str(values, f.DESTINATION_LOCALITY.KEY),
    town: StringUtil.str(values, f.DESTINATION_CITY.KEY),
    county: StringUtil.str(values, f.DESTINATION_COUNTY.KEY),
    deliveryInstructions: '',
    _coordinates: coordinatesFromQuoteValues(
      values,
      f.DESTINATION_ADDRESS.KEY,
      f.DESTINATION_ADDRESS_COORDINATES.KEY
    ),
  };

  const collection = {
    addressId: StringUtil.str(values, f.COLLECTION_ADDRESS_KEY.KEY),
    postcode: StringUtil.str(values, f.COLLECTION_POSTCODE.KEY),
    organisation: StringUtil.str(values, f.COLLECTION_ORGANISATION.KEY),
    street: StringUtil.str(values, f.COLLECTION_STREET.KEY),
    locality: StringUtil.str(values, f.COLLECTION_LOCALITY.KEY),
    town: StringUtil.str(values, f.COLLECTION_CITY.KEY),
    county: StringUtil.str(values, f.COLLECTION_COUNTY.KEY),
    collectionInstructions: '',
    _coordinates: coordinatesFromQuoteValues(
      values,
      f.COLLECTION_ADDRESS.KEY,
      f.COLLECTION_ADDRESS_COORDINATES.KEY
    ),
  };

  return {
    collectionPostcode: collection.postcode,
    deliveryPostcode: delivery.postcode,
    delivery,
    collection,
    bookingMode: hasModalServiceSelection ? 'ready' : '',
    selectedNetworkCode,
    ...(prefetchedVehicleKey ? { prefetchedVehicleKey } : {}),
  };
};

export const formatQuoteValidityLineFromNow = () => {
  const m = moment().tz(DATE_FORMAT.EUROPE_LONDON_TIME_ZONE);
  return `Your quote is valid for 15 minutes from ${m.format(
    'HH:mm'
  )} on ${m.format('D MMMM')}.`;
};

const pickPrice = row => {
  if (row == null || typeof row !== 'object') {
    return undefined;
  }
  const n = v => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v));
  return n(
    row.price ??
      row.amount ??
      row.totalPrice ??
      row.total ??
      row.servicePrice ??
      row.exVatPrice ??
      row.netPrice
  );
};

const vehicleCapacityFrom = cap => {
  if (cap == null || typeof cap !== 'object') {
    return undefined;
  }
  const out = {};
  CAPACITY_KEYS.forEach(key => {
    const v = NumberUtil.num(cap[key]);
    if (v != null) {
      out[key] = v;
    }
  });
  return Object.keys(out).length ? out : undefined;
};

const pickExtendedVehicleDetails = vehicleOrRow => {
  const ext = vehicleOrRow?.extendedVehicleDetails;
  if (ext == null || typeof ext !== 'object') {
    return {};
  }

  const vehicleIconUrl =
    typeof ext.vehicleIcon === 'string' ? ext.vehicleIcon.trim() : undefined;
  const vehicleCapacity = vehicleCapacityFrom(ext.vehicleCapacity);

  return {
    ...(vehicleIconUrl ? { vehicleIconUrl } : {}),
    ...(vehicleCapacity ? { vehicleCapacity } : {}),
  };
};

const mergeVehicleFields = (base, vehicleOrRow) => {
  const extra = pickExtendedVehicleDetails(vehicleOrRow);
  return { ...base, ...extra };
};

const serviceCodeFrom = service => {
  if (typeof service?.serviceCode === 'string' && service.serviceCode.trim()) {
    return service.serviceCode.trim();
  }
  return CITYSPRINT_NETWORK_CODE;
};

const networkDescFromService = (service, vehicle) => {
  const vehicleLabel =
    typeof vehicle?.vehicleDescription === 'string'
      ? vehicle.vehicleDescription
      : '';
  const desc =
    (typeof service?.serviceDescription === 'string' &&
      service.serviceDescription) ||
    'Same Day delivery';
  return vehicleLabel ? `${desc} (${vehicleLabel})` : desc;
};

const legacyNetworkCode = (row, index) => {
  if (typeof row?.serviceCode === 'string' && row.serviceCode) {
    return row.serviceCode;
  }
  if (typeof row?.code === 'string' && row.code) {
    return row.code;
  }
  return index > 0
    ? `${CITYSPRINT_NETWORK_CODE}_${index}`
    : CITYSPRINT_NETWORK_CODE;
};

const legacyNetworkDesc = row => {
  if (typeof row?.description === 'string' && row.description) {
    return row.description;
  }
  if (typeof row?.serviceName === 'string' && row.serviceName) {
    return row.serviceName;
  }
  if (typeof row?.name === 'string' && row.name) {
    return row.name;
  }
  return 'Same Day delivery';
};

const parcelVehicleKeyFrom = (svcCode, vehicle) => {
  if (typeof vehicle?.vehicleId === 'string' && vehicle.vehicleId.trim()) {
    return vehicle.vehicleId.trim();
  }
  if (typeof vehicle?.id === 'string' && vehicle.id.trim()) {
    return vehicle.id.trim();
  }
  return `${svcCode}-${vehicle?.vehicleDescription ?? 'vehicle'}`;
};

// eslint-disable-next-line complexity -- field merge from APIM service + vehicle
const mapServiceAndVehicleToNetwork = (service, vehicle) => {
  const pb = service?.priceBreakdown ?? {};
  const totalInc = NumberUtil.num(pb.totalBookingPrice);
  const totalVat = NumberUtil.num(pb.totalTaxAmount);
  let preTax = NumberUtil.num(pb.preTaxAmount);
  if (
    preTax === undefined &&
    Number.isFinite(totalInc) &&
    Number.isFinite(totalVat)
  ) {
    preTax = Number((totalInc - totalVat).toFixed(2));
  }
  const withVat = totalInc ?? preTax;
  const safeEx = preTax ?? 0;
  const safeInc = withVat ?? safeEx;

  const svcCode = serviceCodeFrom(service);

  const sectionTitle =
    (typeof service?.serviceDescription === 'string' &&
      service.serviceDescription.trim()) ||
    'Same Day delivery';

  const vehicleDescription =
    typeof vehicle?.vehicleDescription === 'string'
      ? vehicle.vehicleDescription.trim()
      : undefined;

  const latestCollectionTime =
    typeof service?.latestCollectionTime === 'string'
      ? service.latestCollectionTime.trim()
      : undefined;
  const latestDeliveryTime =
    typeof service?.latestDeliveryTime === 'string'
      ? service.latestDeliveryTime.trim()
      : undefined;

  return mergeVehicleFields(
    {
      networkCode: String(svcCode),
      networkDesc: networkDescFromService(service, vehicle),
      servicePrice: safeEx,
      servicePriceWithVatRate: safeInc,
      parcelSectionTitle: sectionTitle,
      parcelVehicleKey: parcelVehicleKeyFrom(svcCode, vehicle),
      vehicleDescription,
      serviceDescription: sectionTitle,
      ...(latestCollectionTime ? { latestCollectionTime } : {}),
      ...(latestDeliveryTime ? { latestDeliveryTime } : {}),
    },
    vehicle
  );
};

const toLegacyNetworkRow = (row, index) => {
  const price = pickPrice(row);
  const safePrice = Number.isFinite(price) ? price : 0;
  const code = legacyNetworkCode(row, index);

  return mergeVehicleFields(
    {
      networkCode: String(code),
      networkDesc: legacyNetworkDesc(row),
      servicePrice: safePrice,
      servicePriceWithVatRate: safePrice,
      parcelSectionTitle: legacyNetworkDesc(row),
      parcelVehicleKey: String(code),
    },
    row
  );
};

export const getVehicleQuotes = data => {
  if (data == null) {
    return [];
  }
  if (Array.isArray(data.data?.quotes)) {
    return data.data.quotes;
  }
  if (Array.isArray(data.quotes)) {
    return data.quotes;
  }
  return [];
};

export const mapCitysprintVehicleToNetworkRows = vehicle => {
  if (vehicle == null || typeof vehicle !== 'object') {
    return [];
  }
  const services = vehicle?.availableServices;
  if (Array.isArray(services)) {
    if (!services.length) {
      return [];
    }
    return services.map(service =>
      mapServiceAndVehicleToNetwork(service, vehicle)
    );
  }
  return [toLegacyNetworkRow(vehicle, 0)];
};

export const mapCitysprintQuoteToNetworks = data => {
  const vehicleQuotes = getVehicleQuotes(data);

  if (vehicleQuotes.length) {
    const rows = [];

    vehicleQuotes.forEach(vehicle => {
      rows.push(...mapCitysprintVehicleToNetworkRows(vehicle));
    });

    return rows;
  }

  if (data == null) {
    return [];
  }

  if (Array.isArray(data)) {
    return data.map(toLegacyNetworkRow);
  }

  if (Array.isArray(data.quoteOptions)) {
    return data.quoteOptions.map(toLegacyNetworkRow);
  }

  if (Array.isArray(data.services)) {
    return data.services.map(toLegacyNetworkRow);
  }

  const singlePrice = pickPrice(data);
  if (singlePrice !== undefined && Number.isFinite(singlePrice)) {
    return [toLegacyNetworkRow(data, 0)];
  }

  return [];
};
